import { useEffect, useMemo, useState } from 'react'
import { eq, useLiveQuery } from '@tanstack/react-db'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'

import { ArchiveCustomerDialog } from '@/components/ArchiveCustomerDialog'
import { useDb } from '@/lib/dbContext'
import type { ArchivedServiceType } from '@/db/domain/archivedServices'
import type { FollowUp, FollowUpCategory } from '@/db/domain/followUps'
import type {
  Callout,
  Customer,
  CustomerRevision,
  Job,
  Note,
  StatusChangeLog,
} from '@/lib/schemas/db'
import {
  CustomerDetailsScreen,
  type CustomerDetailsScreenProps,
  type CustomerDocumentRow,
} from '@/screens/CustomerDetailsScreen'
import {
  buildFollowUpThreads,
  buildGeneralNotes,
  buildHistoryEvents,
  type ServiceForThread,
} from '@/screens/customerTimeline'
import { useAuth } from '@/lib/auth'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import type { CustomerDocument, CustomerNote } from '@/db/domain/models'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'

const serviceDetailRoutes = {
  Callout: '/callouts/$id',
  Session: '/sessions/$id',
  Job: '/jobs/$id',
} as const

const archivedServiceTypeLabels: Record<
  ArchivedServiceType,
  'Callout' | 'Session' | 'Job'
> = {
  callout: 'Callout',
  remote: 'Session',
  sitdown: 'Session',
  job: 'Job',
}

function CustomerDetailsScreenContainerInner({
  customerId,
  activeTab,
}: {
  customerId: string
  activeTab: 'details' | 'services' | 'notes' | 'history' | 'documents'
}) {
  const db = useDb()
  const { user } = useAuth()
  const navigate = useNavigate()
  // The selected thread lives in the URL so threads deep-link and the back
  // button walks through them. strict:false — only the notes route carries it.
  const search = useSearch({ strict: false }) as { thread?: string }
  const [photoNoteError, setPhotoNoteError] = useState<string | null>(null)
  const [documentError, setDocumentError] = useState<string | null>(null)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const tryPublicId = looksLikePublicId(customerId)

  const customerByPrivateIdResult = useLiveQuery(
    (q) =>
      q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, customerId))
        .findOne(),
    [customerId],
  )

  const customerByPublicIdResult = useLiveQuery(
    (q) =>
      tryPublicId
        ? q
            .from({ customers: db.customersCollection })
            .where(({ customers }) => eq(customers.publicId, customerId))
            .findOne()
        : null,
    [customerId, tryPublicId],
  )

  const customer =
    (customerByPrivateIdResult.data as Customer | undefined) ??
    (customerByPublicIdResult.data as Customer | undefined)
  const customerPrivateId = customer?.id ?? customerId

  if (!tryPublicId && customer && (customerByPrivateIdResult.data as Customer | undefined)) {
    captureLegacyIdRoute('customer', customerId)
  }

  const calloutsResult = useLiveQuery(
    (q) =>
      q
        .from({ callouts: db.calloutsCollection })
        .where(({ callouts }) => eq(callouts.customerId, customerPrivateId)),
    [customerPrivateId],
  )

  const sessionsResult = useLiveQuery(
    (q) =>
      q
        .from({ sessions: db.sessionsCollection })
        .where(({ sessions }) =>
          eq(sessions.customerId, customerPrivateId),
        ),
    [customerPrivateId],
  )

  const jobsResult = useLiveQuery(
    (q) =>
      q
        .from({ jobs: db.jobsCollection })
        .where(({ jobs }) => eq(jobs.customerId, customerPrivateId)),
    [customerPrivateId],
  )

  const customerNotesResult = useLiveQuery(
    (q) =>
      q
        .from({ notes: db.customerNotesCollection })
        .where(({ notes }) => eq(notes.customerId, customerPrivateId)),
    [customerPrivateId],
  )

  const followUpsResult = useLiveQuery(
    (q) =>
      q
        .from({ followUps: db.followUpsCollection })
        .where(({ followUps }) => eq(followUps.customerId, customerPrivateId)),
    [customerPrivateId],
  )
  const customerDocumentsResult = useLiveQuery(
    (q) =>
      q
        .from({ documents: db.customerDocumentsCollection })
        .where(({ documents }) =>
          eq(documents.customerId, customerPrivateId),
        ),
    [customerPrivateId],
  )
  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))
  const categoriesResult = useLiveQuery((q) =>
    q.from({ categories: db.followUpCategoriesCollection }),
  )

  const customerRevisionsResult = useLiveQuery(
    (q) =>
      q
        .from({ revisions: db.customerRevisionsCollection })
        .where(({ revisions }) =>
          eq(revisions.customerId, customerPrivateId),
        ),
    [customerPrivateId],
  )

  // History's work-note events read straight from the note collections, so
  // edits made elsewhere show without a refetch. Gated to the tab: only
  // History needs service notes, and subscribing starts the note sync.
  const historyTabActive = activeTab === 'history'
  const calloutNotesResult = useLiveQuery(
    (q) => (historyTabActive ? q.from({ notes: db.notesCollection }) : null),
    [historyTabActive],
  )
  const sessionNotesResult = useLiveQuery(
    (q) =>
      historyTabActive ? q.from({ notes: db.sessionNotesCollection }) : null,
    [historyTabActive],
  )
  const jobNotesResult = useLiveQuery(
    (q) => (historyTabActive ? q.from({ notes: db.jobNotesCollection }) : null),
    [historyTabActive],
  )

  const calloutStatusChangesResult = useLiveQuery((q) =>
    q.from({ statusChanges: db.statusChangesCollection }),
  )

  const remoteStatusChangesResult = useLiveQuery((q) =>
    q.from({ statusChanges: db.sessionStatusChangesCollection }),
  )

  const jobStatusChangesResult = useLiveQuery((q) =>
    q.from({ statusChanges: db.jobStatusChangesCollection }),
  )

  // Archived (Invoiced/Cancelled) services are excluded from the live sync,
  // so the services tab fetches them through the archived_services search and
  // reads the rows back from the local archived-rows collection.
  const [archivedKeys, setArchivedKeys] = useState<string[]>([])
  const [archivedError, setArchivedError] = useState(false)

  const archivedRowsResult = useLiveQuery((q) =>
    q.from({ rows: db.archivedServicesCollection }),
  )

  const resolvedCustomerId = customer?.id

  useEffect(() => {
    // Services tab lists archived services; History links back to them too.
    if (
      (activeTab !== 'services' && activeTab !== 'history') ||
      !resolvedCustomerId
    )
      return
    let cancelled = false
    setArchivedError(false)
    db.searchArchivedServices({
      filters: { customerId: resolvedCustomerId },
      sort: { key: 'booked', dir: 'desc' },
      page: 1,
      pageSize: 200,
    })
      .then((result) => {
        if (!cancelled) setArchivedKeys(result.keys)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load archived services for customer', err)
        setArchivedError(true)
      })
    return () => {
      cancelled = true
    }
  }, [db, activeTab, resolvedCustomerId])

  // The due-only sync skips this customer's upcoming and closed follow-ups —
  // load them all so the conversation threads and history stay complete.
  useEffect(() => {
    if (!resolvedCustomerId) return
    db.loadCustomerFollowUps(resolvedCustomerId).catch((err: unknown) => {
      console.error('Failed to load follow-ups for customer', err)
    })
  }, [db, resolvedCustomerId])

  // ---- Conversation & History derivations ---------------------------------
  // Hoisted above the loading guards because they contain hooks, and memoized
  // because a change to any subscribed collection re-renders this container —
  // without memos every note added anywhere would rebuild every thread feed.
  const followUps = useMemo(
    () => (followUpsResult.data ?? []) as FollowUp[],
    [followUpsResult.data],
  )
  const customerNotes = useMemo(
    () => (customerNotesResult.data ?? []) as CustomerNote[],
    [customerNotesResult.data],
  )
  const customerRevisions = useMemo(
    () =>
      [...((customerRevisionsResult.data ?? []) as CustomerRevision[])].sort(
        (a, b) => a.createdAt.localeCompare(b.createdAt),
      ),
    [customerRevisionsResult.data],
  )
  const documentRows: CustomerDocumentRow[] = useMemo(
    () =>
      ((customerDocumentsResult.data ?? []) as CustomerDocument[])
        .filter((doc) => !doc.deletedAt)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map((doc) => ({
          id: doc.id,
          title: doc.title,
          fileUrl: doc.fileUrl,
          size: doc.size,
          uploadedByName: db.getAuthorDisplayName(doc.uploadedBy),
          createdAt: doc.createdAt,
          // The URL is derived from the stored record, so its absence means
          // the upload hasn't landed yet.
          pending: !doc.fileUrl,
        })),
    [customerDocumentsResult.data, db],
  )

  const servicesForThreads: ServiceForThread[] = useMemo(
    () => [
      ...((calloutsResult.data as Callout[] | undefined) ?? []).map(
        (callout) => ({
          id: callout.id,
          routeId: callout.publicId ?? callout.id,
          serviceType: 'Callout' as const,
          title: callout.issueSummary,
          status: callout.status,
          bookedAt: callout.createdAt,
        }),
      ),
      ...((sessionsResult.data as Callout[] | undefined) ?? []).map(
        (session) => ({
          id: session.id,
          routeId: session.publicId ?? session.id,
          serviceType: 'Session' as const,
          title: session.issueSummary,
          status: session.status,
          bookedAt: session.createdAt,
        }),
      ),
      ...((jobsResult.data as Job[] | undefined) ?? []).map((job) => ({
        id: job.id,
        routeId: job.publicId ?? job.id,
        serviceType: 'Job' as const,
        title: job.fault,
        status: job.status,
        bookedAt: job.createdAt,
      })),
    ],
    [calloutsResult.data, sessionsResult.data, jobsResult.data],
  )

  const serviceFetchSignature = servicesForThreads
    .map((service) => service.id)
    .join(',')

  // Backfill notes the collections' sync missed (parents hydrated after it
  // started); the live queries above pick the upserts up automatically.
  useEffect(() => {
    if (activeTab !== 'history' || !serviceFetchSignature) return
    db.loadServiceNotes(
      servicesForThreads.map((service) => ({
        type:
          service.serviceType === 'Callout'
            ? ('callout' as const)
            : service.serviceType === 'Session'
              ? ('session' as const)
              : ('job' as const),
        id: service.id,
      })),
    ).catch((error) => {
      console.error('Failed to load service notes for customer', error)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, serviceFetchSignature, activeTab])

  const statusChangesByServiceId = useMemo(() => {
    const map = new Map<string, StatusChangeLog[]>()
    for (const change of [
      ...(calloutStatusChangesResult.data ?? []),
      ...(remoteStatusChangesResult.data ?? []),
      ...(jobStatusChangesResult.data ?? []),
    ]) {
      const current = map.get(change.serviceId) ?? []
      current.push(change)
      map.set(change.serviceId, current)
    }
    return map
  }, [
    calloutStatusChangesResult.data,
    remoteStatusChangesResult.data,
    jobStatusChangesResult.data,
  ])

  const notesByServiceId = useMemo(() => {
    const map = new Map<string, Note[]>()
    const serviceIds = new Set(servicesForThreads.map((service) => service.id))
    const serviceNotes = [
      ...((calloutNotesResult.data ?? []) as Note[]),
      ...((sessionNotesResult.data ?? []) as Note[]),
      ...((jobNotesResult.data ?? []) as Note[]),
    ]
      .filter((note) => serviceIds.has(note.serviceId))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    for (const note of serviceNotes) {
      const current = map.get(note.serviceId) ?? []
      current.push(note)
      map.set(note.serviceId, current)
    }
    return map
  }, [
    calloutNotesResult.data,
    sessionNotesResult.data,
    jobNotesResult.data,
    servicesForThreads,
  ])

  const followUpThreads = useMemo(
    () => buildFollowUpThreads(followUps, customerNotes, db.getAuthorDisplayName),
    [followUps, customerNotes, db],
  )
  const generalNotes = useMemo(
    () => buildGeneralNotes(customerNotes, db.getAuthorDisplayName),
    [customerNotes, db],
  )

  // History is the heaviest derivation and only exists on its own tab.
  const historyEvents = useMemo(() => {
    if (activeTab !== 'history') return []
    const archivedByKey = new Map(
      (archivedRowsResult.data ?? []).map((row) => [row.key, row]),
    )
    const liveRouteIds = new Set(
      servicesForThreads.map((service) => service.routeId),
    )
    // Archived (Invoiced/Cancelled) services, from the archive rows this tab
    // fetched — minus any service transiently still in the live sync.
    const archivedForHistory = archivedKeys
      .map((key) => archivedByKey.get(key))
      .filter(
        (row): row is NonNullable<typeof row> =>
          Boolean(row?.publicId) && !liveRouteIds.has(row!.publicId!),
      )
      .map((row) => ({
        routeId: row.publicId!,
        serviceType: archivedServiceTypeLabels[row.type],
        title: row.summary,
        status: row.status,
        bookedAt: row.bookedAt,
      }))

    return buildHistoryEvents({
      customerNotes,
      followUps,
      services: servicesForThreads,
      archivedServices: archivedForHistory,
      notesByServiceId,
      statusChangesByServiceId,
      customerRevisions,
      getName: db.getAuthorDisplayName,
    })
  }, [
    activeTab,
    archivedKeys,
    archivedRowsResult.data,
    servicesForThreads,
    notesByServiceId,
    statusChangesByServiceId,
    customerNotes,
    customerRevisions,
    followUps,
    db,
  ])

  if (
    customerByPrivateIdResult.isLoading ||
    customerByPublicIdResult.isLoading ||
    calloutsResult.isLoading ||
    sessionsResult.isLoading ||
    jobsResult.isLoading ||
    calloutStatusChangesResult.isLoading ||
    remoteStatusChangesResult.isLoading ||
    jobStatusChangesResult.isLoading
  ) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-4xl mx-auto px-4 py-6 space-y-2">
          <h1 className="text-2xl font-semibold">Customer details</h1>
          <p className="text-sm text-slate-600">Loading customer…</p>
        </main>
      </div>
    )
  }

  if (
    customerByPrivateIdResult.isError ||
    customerByPublicIdResult.isError ||
    calloutsResult.isError ||
    sessionsResult.isError ||
    jobsResult.isError ||
    calloutStatusChangesResult.isError ||
    remoteStatusChangesResult.isError ||
    jobStatusChangesResult.isError
  ) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-4xl mx-auto px-4 py-6 space-y-2">
          <h1 className="text-2xl font-semibold">Customer details</h1>
          <p className="text-sm text-slate-600">
            Unable to load customer details right now.
          </p>
        </main>
      </div>
    )
  }

  if (!customer) {
    return (
      <div className="bg-slate-50 text-slate-900">
        <main className="max-w-4xl mx-auto px-4 py-6 space-y-2">
          <h1 className="text-2xl font-semibold">Customer details</h1>
          <p className="text-sm text-slate-600">
            Customer not found. Check the link or return to the customers list.
          </p>
        </main>
      </div>
    )
  }

  const calloutStatusChangesById = new Map<string, StatusChangeLog[]>()
  for (const change of calloutStatusChangesResult.data ?? []) {
    const current = calloutStatusChangesById.get(change.serviceId) ?? []
    current.push(change)
    calloutStatusChangesById.set(change.serviceId, current)
  }

  const remoteStatusChangesById = new Map<string, StatusChangeLog[]>()
  for (const change of remoteStatusChangesResult.data ?? []) {
    const current = remoteStatusChangesById.get(change.serviceId) ?? []
    current.push(change)
    remoteStatusChangesById.set(change.serviceId, current)
  }

  const jobStatusChangesById = new Map<string, StatusChangeLog[]>()
  for (const change of jobStatusChangesResult.data ?? []) {
    const current = jobStatusChangesById.get(change.serviceId) ?? []
    current.push(change)
    jobStatusChangesById.set(change.serviceId, current)
  }

  const services: CustomerDetailsScreenProps['services'] = [
    ...(((calloutsResult.data as Callout[] | undefined) ?? []).map((callout) => ({
      id: callout.publicId ?? callout.id,
      startTime: callout.startTime,
      isWholeDay: callout.isWholeDay,
      issueSummary: callout.issueSummary,
      status: callout.status,
      serviceType: 'Callout' as const,
      statusChanges: calloutStatusChangesById.get(callout.id) ?? [],
    }))),
    ...(((sessionsResult.data as Callout[] | undefined) ?? []).map((session) => ({
      id: session.publicId ?? session.id,
      startTime: session.startTime,
      isWholeDay: session.isWholeDay,
      issueSummary: session.issueSummary,
      status: session.status,
      serviceType: 'Session' as const,
      statusChanges: remoteStatusChangesById.get(session.id) ?? [],
    }))),
    ...(((jobsResult.data as Job[] | undefined) ?? []).map((job) => ({
      id: job.publicId ?? job.id,
      startTime: job.createdAt,
      isWholeDay: false,
      issueSummary: job.fault,
      status: job.status,
      serviceType: 'Job' as const,
      statusChanges: jobStatusChangesById.get(job.id) ?? [],
    }))),
  ]

  // A service can transiently be in both the live collections and the archive
  // (e.g. right as it is invoiced) — the live row wins, it has status history.
  const liveServiceIds = new Set(services.map((service) => service.id))
  const archivedRowsByKey = new Map(
    (archivedRowsResult.data ?? []).map((row) => [row.key, row]),
  )
  for (const key of archivedKeys) {
    const row = archivedRowsByKey.get(key)
    if (!row || !row.publicId || liveServiceIds.has(row.publicId)) continue
    services.push({
      id: row.publicId,
      startTime: row.bookedAt,
      isWholeDay: row.isWholeDay,
      issueSummary: row.summary,
      status: row.status,
      serviceType: archivedServiceTypeLabels[row.type],
      statusChanges: [],
    })
  }

  services.sort((a, b) => b.startTime.localeCompare(a.startTime))

  const customerRouteId = customer.publicId ?? customer.id
  const openThread = (key: string) => {
    navigate({
      to: '/customers/$id/notes',
      params: { id: customerRouteId },
      search: key === 'general' ? {} : { thread: key },
    })
  }

  const mergedInto = customer.mergedInto
    ? db.customersCollection.get(customer.mergedInto)
    : undefined

  return (
    <>
    <CustomerDetailsScreen
      onArchive={() => setArchiveOpen(true)}
      onRestore={async () => {
        await db.unarchiveCustomer(customer.id)
      }}
      mergedInto={
        mergedInto
          ? { routeId: mergedInto.publicId ?? mergedInto.id, name: mergedInto.fullName }
          : undefined
      }
      customer={customer}
      services={services}
      archivedServicesError={archivedError}
      activeTab={activeTab}
      getAuthorDisplayName={db.getAuthorDisplayName}
      followUpThreads={followUpThreads}
      generalNotes={generalNotes}
      historyEvents={historyEvents}
      onOpenHistoryLink={(link) => {
        if (link.kind === 'thread') {
          openThread(link.threadKey)
        } else {
          navigate({
            to: serviceDetailRoutes[link.serviceType],
            params: { id: link.routeId },
          })
        }
      }}
      onOpenHistory={() =>
        navigate({
          to: '/customers/$id/history',
          params: { id: customerRouteId },
        })
      }
      selectedThreadKey={search.thread ?? 'general'}
      onSelectThread={openThread}
      onCompleteFollowUp={(id, options) => db.completeFollowUp(id, options)}
      onSnoozeFollowUp={(id, step, options) =>
        db.snoozeFollowUp(id, step, options)
      }
      onRescheduleFollowUp={(id, dueAt, options) =>
        db.rescheduleFollowUp(id, dueAt, options)
      }
      onCloseFollowUp={(id, options) => db.closeFollowUp(id, options)}
      onReopenFollowUp={(id, dueAt, options) =>
        db.reopenFollowUp(id, dueAt, options)
      }
      onCreateFollowUp={(input, extras) => {
        const created = db.createFollowUp(input)
        if (extras.firstNote) {
          db.appendCustomerNote(input.customerId, {
            text: extras.firstNote,
            followUpId: created.id,
          })
        }
        openThread(`fu:${created.id}`)
      }}
      categoryOptions={((categoriesResult.data ?? []) as FollowUpCategory[])
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
        .map((c) => ({ id: c.id, name: c.name }))}
      assigneeOptions={(usersResult.data ?? []).map((staff) => ({
        id: staff.id,
        name: staff.name,
      }))}
      newNoteAuthorName={user?.name ?? ''}
      onAddNote={(text, target) => {
        if (!text.trim()) return false
        db.appendCustomerNote(customer.id, {
          text,
          followUpId:
            target.kind === 'followUp' ? target.followUpId : undefined,
        })
        return true
      }}
      onSaveEditNote={(id, text) =>
        db.updateCustomerNote(customer.id, id, { text })
      }
      onDeleteNote={(id) => db.deleteCustomerNote(customer.id, id)}
      onSubmitPhotoNote={(input, target) => {
        setPhotoNoteError(null)
        const photoInput = {
          caption: input.caption.trim(),
          photoUrl: input.photoUrls[0],
          photoUrls: input.photoUrls,
          photoFiles: input.photoFiles,
        }

        const result = db.appendCustomerNoteWithTransaction(customer.id, {
          ...photoInput,
          followUpId:
            target.kind === 'followUp' ? target.followUpId : undefined,
        })
        const transaction = result.transaction
        const rollback = () => {
          if (db.customerNotesCollection.has?.(result.note.id)) {
            db.discardCustomerNote(result.note.id)
          }
        }

        transaction?.isPersisted?.promise
          ?.then(() => setPhotoNoteError(null))
          .catch((error: unknown) => {
            setPhotoNoteError(
              formatPocketBaseError(error, {
                fallbackMessage:
                  'Unable to save photo note. Please try again.',
                fieldLabels: { photo: 'Photo' },
              }),
            )
            try {
              // Hard delete: the upload failed, so this note never joined the
              // thread and should leave no tombstone.
              rollback()
            } catch {}
          })
      }}
      onReplacePhotoNote={(id, input) =>
        db.updateCustomerNote(customer.id, id, {
          photoUrl: input.photoUrl,
          photoFile: input.photoFile,
        })
      }
      photoNoteError={photoNoteError}
      onClearPhotoNoteError={() => setPhotoNoteError(null)}
      documents={documentRows}
      onUploadDocuments={(files) => {
        setDocumentError(null)
        for (const file of files) {
          const result = db.addCustomerDocumentWithTransaction(customer.id, {
            title: file.name,
            file,
            size: file.size,
          })
          result.transaction?.isPersisted?.promise?.catch((error: unknown) => {
            setDocumentError(
              formatPocketBaseError(error, {
                fallbackMessage: `Unable to upload ${file.name}. Please try again.`,
                fieldLabels: { file: 'File' },
              }),
            )
            try {
              // Hard delete: the upload failed, so this document never joined
              // the record and should leave nothing behind.
              if (db.customerDocumentsCollection.has?.(result.document.id)) {
                db.discardCustomerDocument(result.document.id)
              }
            } catch {}
          })
        }
      }}
      onOpenDocument={(doc) => {
        if (!doc.fileUrl) return
        const fileUrl = doc.fileUrl
        // Open the tab synchronously, inside the click gesture, then point it
        // at the tokened URL — the files are protected, so the bare URL 404s
        // and each open needs a fresh short-lived token.
        const win = window.open('', '_blank')
        db.getFileToken()
          .then((token) => {
            const url = token
              ? `${fileUrl}${fileUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
              : fileUrl
            if (win) win.location.href = url
            else window.open(url, '_blank')
          })
          .catch((error: unknown) => {
            win?.close()
            console.error('Failed to get a file token', error)
            setDocumentError(
              'Unable to open the document right now. Please try again.',
            )
          })
      }}
      onDeleteDocument={(id) => db.deleteCustomerDocument(customer.id, id)}
      documentError={documentError}
      onClearDocumentError={() => setDocumentError(null)}
      revisions={customerRevisions}
      onSaveCustomerDetails={(values) =>
        db.updateCustomerAndWait(customer.id, {
          fullName: values.fullName,
          externalId: values.externalId,
          contactEmail: values.contactEmail,
          contactPhone: values.contactPhone,
          contactAddress: values.contactAddress,
          invoiceName: values.invoiceName,
          invoiceEmail: values.invoiceEmail,
          invoicePhone: values.invoicePhone,
          invoiceAddress: values.invoiceAddress,
          invoiceVat: values.invoiceVat,
        })
      }
    />
    <ArchiveCustomerDialog
      open={archiveOpen}
      customerId={customer.id}
      customerName={customer.fullName}
      onClose={() => setArchiveOpen(false)}
    />
    </>
  )
}

export function CustomerDetailsScreenContainer() {
  const customerId = useParams({ from: '/customers/$id', select: (p) => p.id })
  return (
    <CustomerDetailsScreenContainerInner
      customerId={customerId}
      activeTab="details"
    />
  )
}

export function CustomerNotesScreenContainer() {
  const customerId = useParams({
    from: '/customers/$id/notes',
    select: (p) => p.id,
  })
  return (
    <CustomerDetailsScreenContainerInner
      customerId={customerId}
      activeTab="notes"
    />
  )
}

export function CustomerHistoryScreenContainer() {
  const customerId = useParams({
    from: '/customers/$id/history',
    select: (p) => p.id,
  })
  return (
    <CustomerDetailsScreenContainerInner
      customerId={customerId}
      activeTab="history"
    />
  )
}

export function CustomerDocumentsScreenContainer() {
  const customerId = useParams({
    from: '/customers/$id/documents',
    select: (p) => p.id,
  })
  return (
    <CustomerDetailsScreenContainerInner
      customerId={customerId}
      activeTab="documents"
    />
  )
}

export function CustomerServicesScreenContainer() {
  const customerId = useParams({
    from: '/customers/$id/services',
    select: (p) => p.id,
  })
  return (
    <CustomerDetailsScreenContainerInner
      customerId={customerId}
      activeTab="services"
    />
  )
}
